import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, Check } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Button } from "@core/components/ui/button";
import { useAuth } from "@core/contexts/AuthContext";
import { type AppLocale, SUPPORTED_LOCALES, setLocale } from "@core/i18n";
import { cn } from "@core/lib/utils";
import AccountPageTemplate from "./AccountPageTemplate";

const NAMES: Record<AppLocale, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
};

export default function AccountPreferences() {
  const { t, i18n } = useTranslation("common");
  const { user, updateProfile } = useAuth();
  const [saved, setSaved] = useState(false);

  const current = (i18n.language?.slice(0, 2) as AppLocale) ?? "fr";

  const handleSelect = async (loc: AppLocale) => {
    if (loc === current) return;
    await setLocale(loc);
    // Connecté : persister en base (même valeur que le sélecteur globe du
    // header — source unique `profiles.preferred_language`, aussi utilisée
    // pour la langue des emails).
    if (user) await updateProfile({ preferred_language: loc });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  return (
    <AccountPageTemplate
      title={t("account.preferences.title")}
      description={t("account.preferences.description")}
      testId="account-preferences-page"
    >
      <Card className="border-gray-800 bg-gray-900/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Languages className="h-5 w-5 text-blue-400" />
            {t("account.preferences.language_label")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-400">
            {t("account.preferences.language_help")}
          </p>

          <div
            className="flex flex-col gap-2 sm:flex-row sm:flex-wrap"
            role="radiogroup"
            aria-label={t("account.preferences.language_label")}
          >
            {SUPPORTED_LOCALES.map((loc) => {
              const active = loc === current;
              return (
                <Button
                  key={loc}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  variant={active ? "default" : "outline"}
                  className={cn(
                    "justify-start sm:w-40",
                    active && "ring-2 ring-blue-500",
                  )}
                  onClick={() => void handleSelect(loc)}
                  data-testid={`account-preferences-lang-${loc}`}
                >
                  {active && <Check className="mr-2 h-4 w-4" />}
                  {NAMES[loc]}
                </Button>
              );
            })}
          </div>

          {saved && (
            <p
              className="text-sm text-emerald-300"
              data-testid="account-preferences-saved"
            >
              {t("account.preferences.saved")}
            </p>
          )}
        </CardContent>
      </Card>
    </AccountPageTemplate>
  );
}
